import { beschriftung, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Abschlusszeile, Schrittzeile, Werkzeug } from './typen'

/**
 * Die Schrittfolge eines Konzernabschlusses — **ein Bauteil für beide
 * Bereiche** (R825, 2.13 Abschnitt 10.8).
 *
 * **Der Befund des Auftraggebers vom 2026-09-27:** *„Bei der
 * Schuldenkonsolidierung kann ich derzeit Positionen nicht verändern, wenn ich
 * im KA bin."* Der Knopf fehlte unter dem Konzernabschluß nicht aus einem
 * Grund, sondern weil dieselbe Liste an zwei Stellen im Quelltext stand und
 * die zweite ihn nie bekommen hat.
 *
 * **Was der eine Bereich an Handgriffen anbietet, bietet der andere auch.**
 * Die beiden Bereiche unterscheiden sich nur in dem, was der Rahmen um die
 * Zeile hergibt: Unter dem Konzernabschluß läuft die Folge in der Tabelle der
 * Abschlüsse mit und trägt deren Zustandsspalte, im Vorlagenbereich steht sie
 * in einer eigenen Tabelle. Das ist eine Frage der Spalten und keine der
 * Handgriffe — deshalb steht es hier als <c>bauart</c> und nicht als zweite
 * Liste.
 */

/** Die Werkzeuge, die am Ende und untereinander in ihrer Folge rechnen (R1618). */
const AM_ENDE = new Set(['ausweiskorrektur', 'saldierung'])

/**
 * Die Werkzeuge, deren Abgrenzung der Rechenkern auswertet (R800).
 *
 * **Ein Feld an einem Schritt, der die Angabe übergeht, wäre ein Versprechen
 * ins Leere.** Die Liste wächst mit dem Rechenkern; sie steht hier und nicht
 * im Server, weil der Server die Angabe an jedem Schritt entgegennimmt — er
 * weiß nicht, wer sie liest. Sie steht **einmal** hier und nicht in jedem
 * Bildschirm, der sie braucht.
 */
export const GRENZT_AB = new Set(['schuldenkonsolidierung', 'aufwandertragkonsolidierung'])

/**
 * Die Werkzeuge, die ihr Differenzenkonto am Prozeß tragen (R802) — seit dem
 * 2026-10-10 auch die Konsolidierung von IC-Ausschüttungen (2.9 Abschnitt
 * 13.13, R1511). Sie grenzt nicht in Positionen ab; sie bekommt nur das Konto.
 */
export const MIT_DIFFERENZKONTO = new Set([...GRENZT_AB, 'dividendenkonsolidierung'])

/**
 * Die Werkzeuge, die den Währungsanteil ihrer Differenz auf ein eigenes Konto
 * trennen (R1482). Die Ausschüttung tut das nicht (2.9 Abschnitt 13.13).
 */
export const MIT_WAEHRUNGSKONTO = new Set(['schuldenkonsolidierung', 'aufwandertragkonsolidierung'])

/**
 * Die Werkzeuge, die mit einer Konzernmarge rechnen (2.9 Abschnitt 13.16,
 * R1560–R1562): Sie grenzen in Positionen ab — die Vorräte — und tragen eine
 * Standardmarge und Margen je Lieferbeziehung, kein Differenzenkonto.
 */
export const MIT_MARGE = new Set(['zwischenergebniseliminierung-uv'])

/**
 * **Die Kapitalkonsolidierung wählt ihre Beteiligungs- und
 * Eigenkapitalpositionen** (7.4 Abschnitt 34.12, R1603) — bis zum 2026-10-12
 * Kennzeichen am Konto.
 */
export const MIT_KAPITALPOSITIONEN = new Set(['kapitalkonsolidierung'])

/**
 * **Die Konsolidierung von IC-Ausschüttungen wählt die Positionen der
 * Beteiligungserträge** (R1603) — bis zum 2026-10-12 ein Kennzeichen am Konto.
 */
export const MIT_ERTRAGSPOSITIONEN = new Set(['dividendenkonsolidierung'])

/**
 * Woraus der Gliederungsbaum eines Prozesses geholt wird (R826).
 *
 * **Die Gliederung ist zeitbezogen**, wie jedes Stammdatum: Welche Position es
 * gibt und welches Konto darunter hängt, hängt an Bewertungsebene und Periode.
 * Beides steht am Konzernabschluß, zu dem der Prozeß gehört — und nirgends
 * sonst. Ohne ihn gibt es keinen Baum, und der Prozeß sagt statt dessen, warum.
 */
export function bezugVon(
  abschluesse: readonly Abschlusszeile[] | null,
  schluessel: string | null,
): { ledger: string; periode: number } | null {
  const a = (abschluesse ?? []).find((x) => x.schluessel === schluessel)
  return a ? { ledger: a.ledger, periode: a.bisPeriode } : null
}

/** Der Name eines Werkzeugs, wie ihn die Oberfläche schreibt. */
export function werkzeugname(werkzeug: string): string {
  return t(`werkzeug.${werkzeug}` as Sprachschluessel)
}

export function Schrittfolge({
  bauart,
  folge,
  werkzeuge,
  laeuft,
  aufOeffnen,
  aufEntfernen,
  buchungen,
}: {
  /**
   * Wo die Folge steht: unter einem Konzernabschluß in dessen Tabelle, oder im
   * Vorlagenbereich in einer eigenen.
   */
  bauart: 'abschluss' | 'vorlage'
  folge: readonly Schrittzeile[]
  werkzeuge: readonly Werkzeug[]
  laeuft: boolean
  /** Der Name öffnet das Prozeßfenster — in beiden Bereichen dasselbe (R814). */
  aufOeffnen: (s: Schrittzeile) => void
  /** Ohne Übergabe kein Knopf — wer nicht entfernen darf, bekommt keinen (R1180). */
  aufEntfernen?: (s: Schrittzeile) => void
  /**
   * Die Buchungen eines Schritts im letzten Lauf (R837) — nur im
   * Vorlagenbereich, wo sonst keine Zahl an der Zeile stünde. `null`: keine
   * Zahl, die seine wäre.
   */
  buchungen?: (s: Schrittzeile) => number | null
}) {
  return (
    <>
      {folge.map((s, i) => {
        const werkzeug = werkzeuge.find((w) => w.schluessel === s.werkzeug)
        const name = beschriftung(s.namen) || s.schluessel

        const namenszelle = (
          <>
            {/*
              **Der Name ist der Weg ins Fenster** (R814): Ein eigener Knopf
              *Bearbeiten* daneben beantwortete dieselbe Frage ein zweites Mal.
            */}
            <button type="button" className="prozessname" onClick={() => aufOeffnen(s)}>
              {name}
            </button>
            {/*
              **Die Marke steht an der Zeile und nicht erst im Fenster**
              (R817): Wer die Folge aufklappt, sieht ohne einen einzigen Klick,
              welcher Prozeß vom Konzernhandbuch abweicht.

              **Eine Marke für die Abgrenzung stand hier einen Bau lang und
              ist wieder weg** (Auftrag vom 2026-09-27): *„Für den Anwender ist
              das auf der Ebene nicht hilfreich. Er sieht beim Klick in den
              Prozess die Konfig und dass dort explizite Inhalte ausgewählt
              sind."* Die Abweichung vom Konzernhandbuch ist eine Auskunft, die
              man **suchen** müßte; eine gesetzte Abgrenzung ist der Normalfall
              und steht einen Klick entfernt ausgeschrieben da.
            */}
            {s.uebersteuerungJson !== null && (
              <span className="marke-abweichung" title={t('prozess.weichtab.titel')}>
                {t('prozess.weichtab')}
              </span>
            )}
            {/*
              **Was keinen Vorgang hat, sagt das** (R837): IC-Abstimmung,
              latente Steuern und die Konsolidierung von Dividenden stehen in
              der Folge, damit sie vollständig ist — gerechnet wird für sie
              nichts, und sie stehen deshalb am Ende. Der Vermerk „rechnet am
              Ende" ist mit der Rechenfolge als Ordnung überflüssig geworden:
              Was am Ende rechnet, steht am Ende.
            */}
            {werkzeug && werkzeug.vorgaenge.length === 0 && (
              <span className="klein gedaempft" title={t('schritte.rechnetnicht.erlaeuterung')}>
                {' · '}
                {t('schritte.rechnetnicht')}
              </span>
            )}
            {/*
              **Ausweiskorrektur und Saldierung tragen den Vermerk doch**
              (2.9 Abschnitt 15.9, R1618): Sie rechnen am Ende und
              untereinander in ihrer Folge — wer sie vertauscht, bekommt einen
              anderen Abschluss. Das ist die Auflage zur Entscheidung.
            */}
            {AM_ENDE.has(s.werkzeug) && (
              <span className="klein gedaempft" title={t('prozess.amende.vermerk')}>
                {' · '}
                {t('schritte.amende')}
              </span>
            )}
          </>
        )

        {/*
          **Keine Pfeile mehr** (R837, Entscheidung vom 2026-09-29): Die Folge
          ist die des Programms, und ein Pfeil, der nichts bewirkt, ist ein
          totes Bedienelement.
        */}
        const handgriffe = (
          <td className="handgriff">
            {aufEntfernen && (
              <button
                type="button"
                className="schmal gefahr"
                disabled={laeuft}
                onClick={() => aufEntfernen(s)}
              >
                {t('schritte.entfernen')}
              </button>
            )}
          </td>
        )

        return (
          <tr className="prozesszeile" key={s.schluessel}>
            <td className="nummer">{i + 1}</td>

            {bauart === 'abschluss' ? (
              <>
                <td colSpan={2}>{namenszelle}</td>
                {/*
                  **Das Zeichen steht in derselben Spalte wie oben** (Auftrag
                  vom 2026-09-27): „Ich sehe dann sofort, wenn es einen Fehler
                  gibt, welcher Prozeß ihn verursacht hat."
                */}
                <td className="zustandsspalte">
                  <span
                    className={`ampel ${
                      s.zustand === 'fertig' ? 'gut' : s.zustand === 'unvollstaendig' ? 'schlecht' : 'fehlt'
                    }`}
                    title={t(`prozess.zustand.${s.zustand}` as Sprachschluessel)}
                    aria-label={t(`prozess.zustand.${s.zustand}` as Sprachschluessel)}
                    role="img"
                  />
                </td>
                <td />
              </>
            ) : (
              <>
                <td>
                  {namenszelle}
                  {/*
                    **Das Werkzeug steht nur da, wo es etwas anderes sagt als
                    der Name daneben.** Ab Werk heißt jeder Schritt wie sein
                    Werkzeug; eine eigene Spalte, die elfmal dasselbe
                    wiederholte wie die links, ist seit dem 2026-09-29 weg.
                  */}
                  {name !== werkzeugname(s.werkzeug) && (
                    <span className="klein gedaempft">
                      {' · '}
                      {werkzeugname(s.werkzeug)}
                    </span>
                  )}
                </td>
                {/*
                  **Die Zahl der zweiten Liste** (R837): Bis zum 2026-09-29
                  stand unter der Folge eine zweite Liste mit den Vorgängen des
                  Laufs — dieselben Schritte in anderer Reihenfolge. Was von ihr
                  eine Frage beantwortete, war die Zahl der Buchungen; sie
                  steht jetzt hier.
                */}
                {buchungen && (
                  <td className="zahl gedaempft">
                    {(() => {
                      const zahl = buchungen(s)
                      return zahl === null ? '' : String(zahl)
                    })()}
                  </td>
                )}
              </>
            )}

            {handgriffe}
          </tr>
        )
      })}
    </>
  )
}
